/**
 * Dirty-form guard hook. Two responsibilities:
 *   1. Publish the form's dirty state into the global registry (DirtyFormProvider)
 *      so the centralized useBlocker can intercept cross-pathname navigation
 *      and so non-form-aware code (AppShell's guardedLogout) can call anyDirty().
 *   2. Install a window `beforeunload` listener so tab close / hard reload
 *      surfaces the browser's native "leave page?" dialog when the form is
 *      dirty.
 *   3. Provide a confirmDiscard helper for explicit in-app navigation paths
 *      (e.g. a Cancel/Back button that wants to prompt before exiting).
 *
 * In-app router navigation (sidebar clicks, NavLinks, back/forward, programmatic
 * navigate(), Link clicks) is intercepted ONCE at the provider level — see
 * DirtyFormProvider's useBlocker. This hook does NOT call useBlocker itself.
 * That centralization (Task #258, 2026-06-04) eliminated the
 * "router only supports one blocker at a time" warning that fired under
 * React StrictMode when multiple pages each registered their own blocker.
 *
 * Usage:
 *   const form = useForm({...});
 *   const confirmDiscard = useDirtyFormGuard(form.isDirty());
 *
 *   // Callback-style for explicit Cancel/Back buttons:
 *   <Button onClick={() => confirmDiscard(() => navigate(-1))}>Back</Button>
 *
 *   // Optional saver (Pedro 2026-10-10): when the user navigates away, the guard
 *   // also offers "Save & leave", which calls it (resolve true = all saved).
 *   useDirtyFormGuard(isDirty, saveAll);
 *
 * History:
 *   - Task #100 — first attempt at useBlocker (boolean form) caused render-loop
 *     freezes; deferred.
 *   - Task #251 Stage 2 — re-enabled useBlocker per-hook using the function form;
 *     worked, but produced one StrictMode warning per page.
 *   - Task #258 — moved useBlocker to DirtyFormProvider so there's exactly one
 *     blocker registration app-wide. This is the current architecture.
 */
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useId, useRef } from 'react';
import { useDirtyForm, type DirtySaver } from '../components/DirtyFormProvider';

const sleep = (ms: number) => new Promise((r) => { setTimeout(r, ms); });

/** The form's fields marked invalid, as "Label — error" (Mantine sets aria-invalid, links the
 *  label by `for` and the message by `<id>-error`). Fields on an unmounted tab are not seen. */
function invalidFields(form: HTMLFormElement): string[] {
  return [...form.querySelectorAll<HTMLElement>('[aria-invalid="true"]')].map((el) => {
    const label = el.id ? document.querySelector(`label[for="${CSS.escape(el.id)}"]`)?.textContent : null;
    const err = el.id ? document.getElementById(`${el.id}-error`)?.textContent : null;
    const name = (label ?? el.getAttribute('aria-label') ?? 'A field').replace(/\s*\*\s*$/, '').trim();
    return err?.trim() ? `${name}: ${err.trim()}` : name;
  });
}

/** Smoke 2026-10-11 (S10, Pedro): "Save & leave" that could not save no longer stays
 *  silently — it says what stopped it and what the options are. */
function reportNotSaved(form: HTMLFormElement | null) {
  const fields = form ? invalidFields(form) : [];
  notifications.show({
    color: 'red', autoClose: 12_000,
    title: 'Not saved — you are still on this page',
    message: fields.length
      ? `Fix ${fields.length === 1 ? 'this field' : 'these fields'} first: ${fields.join('; ')}. `
        + 'Then press Save, or leave again and choose "Discard & leave".'
      : 'Something stopped the save (the page shows what — it may be on another tab). Fix it and '
        + 'press Save, or leave again and choose "Discard & leave".',
  });
}

/**
 * A saver for record pages (Pedro 2026-10-10: "Save & leave" on every record page): it presses
 * the page's own Save — submits the page's <form id=…>, so EXACTLY the Save button's path runs
 * (validation, confirmations, maker-checker, staged relationship operations) — then waits.
 * Resolves true once the page has nothing unsaved and no request is running (leave), false
 * if the save stops with changes still pending — an error, a cancelled confirmation, or a
 * dialog the user must finish (stay). It can only err towards staying: pages clear their
 * unsaved state only after a successful save, so it never leaves unsaved work.
 * Pass `enabled=false` for a not-yet-created record (its create navigates to the new record).
 */
export function useFormSubmitSaver(
  formId: string, isDirty: boolean, enabled = true,
): DirtySaver | undefined {
  const qc = useQueryClient();
  const dirtyRef = useRef(isDirty);
  useEffect(() => { dirtyRef.current = isDirty; });
  const saver = useCallback<DirtySaver>(async () => {
    const el = document.getElementById(formId);
    if (!(el instanceof HTMLFormElement)) { reportNotSaved(null); return false; }
    el.requestSubmit();
    await sleep(300);                       // let the submit start its requests
    const started = Date.now();
    let idleSince: number | null = null;
    while (Date.now() - started < 60_000) {
      const busy = qc.isMutating() > 0;
      if (!busy && !dirtyRef.current) return true;
      if (busy) idleSince = null;
      else if (idleSince === null) idleSince = Date.now();
      else if (Date.now() - idleSince > 2_000) { reportNotSaved(el); return false; }   // stopped, still unsaved
      await sleep(100);
    }
    reportNotSaved(el);
    return false;
  }, [formId, qc]);
  return enabled ? saver : undefined;
}

export type ConfirmDiscardFn = (onConfirm?: () => void) => void;

export function useDirtyFormGuard(isDirty: boolean, onSave?: DirtySaver): ConfirmDiscardFn {
  const { confirmDiscardAsync, registerDirty } = useDirtyForm();
  const id = useId();
  // The saver closes over the page's latest state, so keep the newest one in a
  // ref and register a stable wrapper (re-registering on every render is not needed).
  const saveRef = useRef(onSave);
  useEffect(() => { saveRef.current = onSave; });
  const hasSaver = !!onSave;

  // Publish dirty state to the global registry. The provider's centralized
  // useBlocker reads anyDirty() (which aggregates this registry) on every
  // navigation attempt. AppShell's guardedLogout also reads anyDirty()
  // before allowing the session to clear.
  useEffect(
    () => registerDirty(
      id, isDirty,
      hasSaver ? () => saveRef.current?.() ?? Promise.resolve(false) : undefined,
    ),
    [registerDirty, id, isDirty, hasSaver],
  );

  // Tab close / hard reload guard. Modern browsers ignore the message and
  // show their own; assigning returnValue is what triggers the dialog.
  useEffect(() => {
    if (!isDirty) return;
    function onBeforeUnload(e: BeforeUnloadEvent) {
      e.preventDefault();
      e.returnValue = '';
    }
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  return useCallback<ConfirmDiscardFn>((onConfirm) => {
    if (!isDirty) { onConfirm?.(); return; }
    void confirmDiscardAsync().then((ok) => {
      if (ok) onConfirm?.();
    });
  }, [isDirty, confirmDiscardAsync]);
}
